// Formulare de export — generează PDF (print) / CSV / XLSX pentru tratament și
// pentru raportul medical complet pe o perioadă aleasă.
import React, { useState } from "react";
import { t } from './i18n';
import { fmAlert } from './dialog';
import { escapeHtml, fmtD, shouldMedBeActiveOnDate } from './helpers';
import { Dropdown, DateField } from './ui';

export function ExportTratamentForm({meds, medLogs, symptoms, patient, metrics, events, onClose}) {
  const now = new Date();
  const monthNames = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie",
    "Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"].map(m=>t(m));

  // Calculează lunile/anii cu date reale
  const availableYears = React.useMemo(()=>{
    const seen = new Set();
    Object.keys(medLogs).forEach(dk=>{ if(dk) seen.add(dk.slice(0,4)); });
    symptoms.forEach(s=>{ if(s.date) seen.add(s.date.slice(0,4)); });
    Object.keys(events||{}).forEach(dk=>{ if(dk) seen.add(dk.slice(0,4)); });
    if(seen.size===0) seen.add(String(now.getFullYear()));
    return [...seen].sort().reverse().map(Number);
    // Intenționat fără `now`: e un `new Date()` nou la fiecare randare — includerea lui ar anula memo-ul.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[medLogs,symptoms,events]);

  const [selYear,setSelYear]=useState(availableYears[0]||now.getFullYear());
  const [selMonth,setSelMonth]=useState(now.getMonth());
  const [format,setFormat]=useState("pdf");

  function getDaysInMonth(y,m){return new Date(y,m+1,0).getDate();}
  function dayKey(y,m,d){return `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;}

  function getMedDoses(med,dk){
    const log=medLogs[dk];
    if(!log||!log[med.id]) return null; // null = no log at all
    return log[med.id];
  }

  function doseSymbol(dose, dk, medHasAnyLog){
    const today=new Date().toISOString().slice(0,10);
    if(!dose){
      if(dk>today) return "✓"; // viitor planificat = bifa gri
      if(!medHasAnyLog) return ""; // niciun log in luna = gol
      return ""; // trecut fara log = gol
    }
    if(dose.status==="done") return "✓";
    if(dose.status==="skipped") return "✗";
    if(dk<today) return "?"; // pending dar ziua a trecut
    return "✓"; // viitor pending = bifa gri
  }

  function medWasScheduled(med, dk) {
    return shouldMedBeActiveOnDate(med, dk) && med.scheduleType!=="ondemand";
  }

  function medHasAnyLogInMonth(med){
    const days=getDaysInMonth(selYear,selMonth);
    for(let d=1;d<=days;d++){
      const dk=dayKey(selYear,selMonth,d);
      const log=medLogs[dk];
      if(log&&log[med.id]&&log[med.id].length>0) return true;
    }
    return false;
  }

  function getMedDescription(med) {
    const doseAmt=med.schedule?.[0]?.dose||1;
    const form=med.form||"unitate";
    const formLabel={"comprimate":"comprimat","capsule":"capsulă","fiole":"fiolă",
      "plasturi":"plasture","picaturi":"picătură","spray":"puf",
      "supozitoare":"supozitor","injectabil":"injecție","solutie":"ml"}[form]||form;
    const doseStr=`${doseAmt} ${formLabel}`;
    let freqStr="";
    if(med.scheduleType==="ondemand") return t("La nevoie");
    if(med.scheduleType==="interval_hours") freqStr=`${t("o dată la")} ${med.intervalHours||8}h`;
    else if(med.scheduleType==="specific_days"){
      const days=(med.weekDays||[]).map(d=>t(["Lun","Mar","Mie","Joi","Vin","Sâm","Dum"][d])).join(", ");
      freqStr=`${t("săptămânal:")} ${days}`;
    } else if(med.scheduleType==="cycle") freqStr=`${t("ciclu")} ${med.cyclicOn||21}/${med.cyclicOff||7} ${t("zile")}`;
    else {
      const cnt=(med.schedule||[]).length;
      freqStr=cnt===1?t("o dată pe zi"):cnt===2?t("de 2 ori pe zi"):cnt===3?t("de 3 ori pe zi"):`${t("de")} ${cnt} ${t("ori pe zi")}`;
    }
    const times=(med.schedule||[]).map(s=>s.time);
    const timesStr=times.length>0?`, ${t("la ora")} ${times.join(" "+t("și")+" ")}`:"";
    return `${doseStr}, ${freqStr}${timesStr}`;
  }

  function getMedsWithLogs(){
    const days=getDaysInMonth(selYear,selMonth);
    return meds.filter(med=>{
      for(let d=1;d<=days;d++){
        const dk=dayKey(selYear,selMonth,d);
        const log=medLogs[dk];
        if(log&&log[med.id]&&log[med.id].length>0) return true;
      }
      return false;
    });
  }

  // Notite din doze SI din events (isNote)
  function getDayNotes(dk){
    const fromDoses=Object.values(medLogs[dk]||{}).flat()
      .filter(d=>d.note).map(d=>d.note);
    const fromEvents=(events[dk]||[])
      .filter(e=>e.isNote&&e.visitNote).map(e=>e.visitNote);
    return [...fromDoses,...fromEvents].join("; ");
  }

  function getMonthMetrics(){
    const prefix=`${selYear}-${String(selMonth+1).padStart(2,"0")}`;
    return (metrics||[]).filter(m=>m.date?.startsWith(prefix));
  }

  // Grafic ASCII simplu pentru export PDF

  async function exportExcel(){
    const _xlsx=await import('xlsx'); const XLSX=_xlsx.default||_xlsx;
    const wb=XLSX.utils.book_new();
    const days=getDaysInMonth(selYear,selMonth);

    // Sheet 1: Plan
    const planRows=[[t("Medicament"),t("Administrare"),t("Indicație")]];
    meds.filter(m=>m.active&&m.scheduleType!=="ondemand").forEach(med=>{
      planRows.push([med.name,getMedDescription(med),med.indication||""]);
    });
    const ws1=XLSX.utils.aoa_to_sheet(planRows);
    ws1['!cols']=[{wch:25},{wch:50},{wch:30}];
    XLSX.utils.book_append_sheet(wb,ws1,t("Plan Tratament"));

    // Sheet 2: Jurnal
    const jurnalMeds=getMedsWithLogs();
    const jurnalRows=[[t("Medicament"),t("Doze"),...Array.from({length:days},(_,i)=>String(i+1))]];
    jurnalMeds.forEach(med=>{
      const schedule=med.scheduleType==="ondemand"?[{time:t("La nevoie"),dose:"1"}]:(med.schedule||[{time:"",dose:"1"}]);
      const dozeTxt=schedule.map(s=>`${s.time} x${s.dose||1}`).join("; ");
      const hasLog=medHasAnyLogInMonth(med);
      const row=[med.name,dozeTxt];
      for(let d=1;d<=days;d++){
        const dk=dayKey(selYear,selMonth,d);
        const doses=getMedDoses(med,dk);
        if(!doses && !medWasScheduled(med,dk)){
          row.push("");
        } else {
          const syms=schedule.map((_,si)=>{
            const dose=doses?.[si];
            return doseSymbol(dose,dk,hasLog);
          }).join("/");
          row.push(syms);
        }
      }
      jurnalRows.push(row);
    });
    // Simptome si notite
    jurnalRows.push([]);
    jurnalRows.push([t("Simptome și notițe")]);
    jurnalRows.push([t("Data"),t("Simptome"),t("Notițe")]);
    for(let d=1;d<=days;d++){
      const dk=dayKey(selYear,selMonth,d);
      const daySx=symptoms.filter(s=>s.date===dk).map(s=>(s.items||[]).join(", ")).filter(Boolean).join("; ");
      const dayN=getDayNotes(dk);
      if(daySx||dayN) jurnalRows.push([`${d} ${monthNames[selMonth]}`,daySx||"",dayN||""]);
    }
    // Metrics
    const monthMetrics=getMonthMetrics();
    if(monthMetrics.length>0){
      jurnalRows.push([]);
      jurnalRows.push([t("Valori medicale")]);
      jurnalRows.push([t("Data"),t("Tip"),t("Valoare"),t("Notă")]);
      monthMetrics.sort((a,b)=>a.date.localeCompare(b.date)).forEach(m=>{
        const parts=[];
        if(m.ts_sys!=null||m.ts_dia!=null) parts.push(`${m.ts_sys||"?"}/${m.ts_dia||"?"} mmHg`);
        if(m.glicemie!=null) parts.push(`${m.glicemie} mg/dL`);
        if(m.greutate!=null) parts.push(`${m.greutate} kg`);
        const val=parts.join("; ")||"—";
        const tip=[m.ts_sys!=null?t("Tensiune"):"",m.glicemie!=null?t("Glicemie"):"",m.greutate!=null?t("Greutate"):""].filter(Boolean).join(", ");
        jurnalRows.push([m.date,tip,val,m.note||""]);
      });
    }
    const ws2=XLSX.utils.aoa_to_sheet(jurnalRows);
    ws2['!cols']=[{wch:25},{wch:20},...Array(days).fill({wch:4})];
    XLSX.utils.book_append_sheet(wb,ws2,t("Jurnal"));
    XLSX.writeFile(wb,`tratament_${monthNames[selMonth]}_${selYear}.xlsx`);
    onClose();
  }

  function exportPDF(){
    const days=getDaysInMonth(selYear,selMonth);
    const today=new Date().toISOString().slice(0,10);
    const jurnalMeds=getMedsWithLogs();

    let html=`<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${t("Plan Tratament")} - ${monthNames[selMonth]} ${selYear}</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:10px;margin:20px;color:#111}
      h1{font-size:16px;margin-bottom:4px}
      h2{font-size:13px;margin:16px 0 6px;color:#374151;border-bottom:1px solid #e2e8f0;padding-bottom:3px}
      p{font-size:11px;color:#6b7280;margin:0 0 10px}
      table{border-collapse:collapse;width:100%;margin-bottom:16px}
      th{background:#1e293b;color:#fff;padding:4px 6px;text-align:left;font-size:9px}
      th.c{text-align:center}
      td{border:1px solid #e2e8f0;padding:3px 5px;font-size:9px;vertical-align:middle}
      td.c{text-align:center}
      .done{color:#16a34a;font-weight:700}
      .skipped{color:#9ca3af}
      .unknown{color:#9ca3af}
      .planned{color:#d1d5db}
      .legend{display:flex;gap:16px;margin-bottom:10px;font-size:10px}
      .leg{display:flex;align-items:center;gap:4px}
      @media print{body{margin:8px}}
    </style></head><body>`;

    html+=`<h1>${t("Plan de Tratament")} — ${monthNames[selMonth]} ${selYear}</h1>`;
    if(patient?.name) html+=`<p>${t("Pacient:")} ${patient.name}${patient.dob?` · N: ${patient.dob}`:""}</p>`;
    html+=`<div class="legend">
      <div class="leg"><span class="done">✓</span> ${t("Confirmat")}</div>
      <div class="leg"><span class="skipped">✗</span> ${t("Sărit")}</div>
      <div class="leg"><span class="unknown">?</span> ${t("Neconfirmat (zi trecută)")}</div>
      <div class="leg"><span class="planned">✓</span> ${t("Planificat")}</div>
    </div>`;

    // Plan activ
    html+=`<h2>${t("Plan Tratament Activ")}</h2>
    <table><thead><tr><th>${t("Medicament")}</th><th>${t("Administrare")}</th><th>${t("Indicație")}</th></tr></thead><tbody>`;
    meds.filter(m=>m.active&&m.scheduleType!=="ondemand").forEach(med=>{
      html+=`<tr><td><strong>${escapeHtml(med.name)}</strong></td><td>${escapeHtml(getMedDescription(med))}</td><td style="color:#6b7280">${escapeHtml(med.indication)||"—"}</td></tr>`;
    });
    html+=`</tbody></table>`;

    // Jurnal
    html+=`<h2>${t("Jurnal")}</h2>
    <table><thead><tr><th>${t("Medicament")}</th><th class="c">${t("Doze")}</th>`;
    for(let d=1;d<=days;d++) html+=`<th class="c">${d}</th>`;
    html+=`</tr></thead><tbody>`;
    jurnalMeds.forEach(med=>{
      const schedule=med.scheduleType==="ondemand"?[{time:t("La nevoie"),dose:"1"}]:(med.schedule||[{time:"",dose:"1"}]);
      const dozeTxt=schedule.map(s=>`${s.time} x${s.dose||1}`).join("; ");
      const hasLog=medHasAnyLogInMonth(med);
      html+=`<tr><td><strong>${escapeHtml(med.name)}</strong></td><td class="c" style="white-space:nowrap;font-size:8px">${escapeHtml(dozeTxt)}</td>`;
      for(let d=1;d<=days;d++){
        const dk=dayKey(selYear,selMonth,d);
        const doses=getMedDoses(med,dk);
        if(!doses && !medWasScheduled(med,dk)){
          html+=`<td class="c"></td>`;
        } else {
          const syms=schedule.map((_,si)=>{
            const dose=doses?.[si];
            const sym=doseSymbol(dose,dk,hasLog);
            if(!sym) return "";
            const cls=!dose?(dk>today?"planned":""):dose.status==="done"?"done":dose.status==="skipped"?"skipped":dk<today?"unknown":"planned";
            return `<span class="${cls}">${sym}</span>`;
          }).filter(Boolean).join("<br>");
          html+=`<td class="c">${syms}</td>`;
        }
      }
      html+=`</tr>`;
    });
    html+=`</tbody></table>`;

    // Simptome si notite
    const prefix=`${selYear}-${String(selMonth+1).padStart(2,"0")}`;
    const hasSxOrNotes=symptoms.some(s=>s.date?.startsWith(prefix))||
      [...Array(getDaysInMonth(selYear,selMonth))].some((_,i)=>getDayNotes(dayKey(selYear,selMonth,i+1)));
    if(hasSxOrNotes){
      html+=`<h2>${t("Simptome și notițe")}</h2>
      <table><thead><tr><th>${t("Data")}</th><th>${t("Simptome")}</th><th>${t("Notițe")}</th></tr></thead><tbody>`;
      for(let d=1;d<=days;d++){
        const dk=dayKey(selYear,selMonth,d);
        const daySx=symptoms.filter(s=>s.date===dk).map(s=>(s.items||[]).join(", ")).filter(Boolean).join("; ");
        const dayN=getDayNotes(dk);
        if(daySx||dayN) html+=`<tr><td style="white-space:nowrap">${d} ${monthNames[selMonth]}</td><td>${escapeHtml(daySx)||"—"}</td><td>${escapeHtml(dayN)||"—"}</td></tr>`;
      }
      html+=`</tbody></table>`;
    }

    // Valori medicale - grafice SVG pure
    const allMetrics=metrics||[];
    const tensAll=allMetrics.filter(m=>m.ts_sys!=null||m.ts_dia!=null).sort((a,b)=>a.date.localeCompare(b.date));
    const glicAll=allMetrics.filter(m=>m.glicemie!=null).sort((a,b)=>a.date.localeCompare(b.date));
    const greutAll=allMetrics.filter(m=>m.greutate!=null).sort((a,b)=>a.date.localeCompare(b.date));

    function makeSVGChart(title,data,series,unit){
      if(!data||data.length===0) return "";
      const W=520,H=130,PL=42,PR=12,PT=20,PB=28;
      const cW=W-PL-PR,cH=H-PT-PB;
      const allVals=series.flatMap(s=>data.map(p=>p[s.key]).filter(v=>v!=null));
      if(allVals.length===0) return "";
      const minV=Math.min(...allVals),maxV=Math.max(...allVals);
      const pad=(maxV-minV)*0.1||5;
      const lo=minV-pad,hi=maxV+pad;
      const xs=i=>PL+(data.length>1?i*cW/(data.length-1):cW/2);
      const ys=v=>PT+cH-(v-lo)/(hi-lo)*cH;
      const hasLegend=series.length>1;
      const svgH=hasLegend?H+22:H;
      let s=`<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${svgH}" style="display:block;margin-bottom:16px;border:1px solid #e5e7eb;background:#fff">`;
      s+=`<text x="${PL}" y="13" font-size="10" font-weight="bold" fill="#111">${title}</text>`;
      [lo,(lo+hi)/2,hi].forEach(v=>{
        const y=ys(v);
        s+=`<line x1="${PL}" y1="${y.toFixed(1)}" x2="${W-PR}" y2="${y.toFixed(1)}" stroke="#e5e7eb" stroke-width="0.8"/>`;
        s+=`<text x="${PL-4}" y="${(y+4).toFixed(1)}" font-size="8" fill="#6b7280" text-anchor="end">${Math.round(v)}</text>`;
      });
      const step=Math.max(1,Math.floor(data.length/8));
      data.forEach((p,i)=>{
        if(i%step===0||i===data.length-1)
          s+=`<text x="${xs(i).toFixed(1)}" y="${(H-4).toFixed(1)}" font-size="8" fill="#6b7280" text-anchor="middle">${fmtChartDate(p.date)}</text>`;
      });
      series.forEach((sr,si)=>{
        const pts=data.map((p,i)=>p[sr.key]!=null?`${xs(i).toFixed(1)},${ys(p[sr.key]).toFixed(1)}`:null).filter(Boolean);
        if(pts.length>1) s+=`<polyline points="${pts.join(" ")}" fill="none" stroke="${sr.color}" stroke-width="2" stroke-linejoin="round"/>`;
        data.forEach((p,i)=>{ if(p[sr.key]!=null) s+=`<circle cx="${xs(i).toFixed(1)}" cy="${ys(p[sr.key]).toFixed(1)}" r="3" fill="${sr.color}"/>`; });
        if(hasLegend){
          const lx=PL+si*160;
          s+=`<line x1="${lx}" y1="${H+12}" x2="${lx+12}" y2="${H+12}" stroke="${sr.color}" stroke-width="2"/>`;
          s+=`<text x="${lx+16}" y="${H+16}" font-size="9" fill="#374151">${sr.label}</text>`;
        }
      });
      s+=`</svg>`;
      return s;
    }

    const MON=["Ian","Feb","Mar","Apr","Mai","Iun","Iul","Aug","Sep","Oct","Nov","Dec"].map(m=>t(m));
    function fmtChartDate(iso){
      const [y,m,d]=iso.split("-");
      return `${+d}${MON[+m-1]}'${y.slice(2)}`;
    }
    function getChartData(allData){
      const prefix=`${selYear}-${String(selMonth+1).padStart(2,"0")}`;
      const monthD=allData.filter(m=>m.date.startsWith(prefix));
      return monthD.length>2?monthD:allData.slice(-30);
    }

    const tensData=getChartData(tensAll);
    const glicData=getChartData(glicAll);
    const greutData=getChartData(greutAll);

    const tensChart=makeSVGChart(t("Tensiune arterială"),tensData,[{key:"ts_sys",color:"#3b82f6",label:t("Sistolică")},{key:"ts_dia",color:"#f97316",label:t("Diastolică")}],"mmHg");
    const glicChart=makeSVGChart(t("Glicemie"),glicData,[{key:"glicemie",color:"#f59e0b",label:t("Glicemie")}],"mg/dL");
    const greutChart=makeSVGChart(t("Greutate"),greutData,[{key:"greutate",color:"#10b981",label:t("Greutate")}],"kg");

    if(tensChart||glicChart||greutChart){
      html+=`<h2>${t("Valori medicale")}</h2>`+tensChart+glicChart+greutChart;
    }

    html+=`</body></html>`;
    const win=window.open("","_blank");
    win.document.write(html);
    win.document.close();
    win.print();
    onClose();
  }

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
        <div>
          <div className="lbl">{t("Luna")}</div>
          <Dropdown value={selMonth} onChange={v=>setSelMonth(+v)} options={monthNames.map((m,i)=>({v:i,l:m}))}/>
        </div>
        <div>
          <div className="lbl">{t("Anul")}</div>
          <Dropdown value={selYear} onChange={v=>setSelYear(+v)} options={availableYears.map(y=>({v:y,l:String(y)}))}/>
        </div>
      </div>
      <div className="lbl" style={{marginBottom:8}}>{t("Format")}</div>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {[["pdf","📄 PDF"],["excel","📊 Excel"]].map(([k,l])=>(
          <button key={k} onClick={()=>setFormat(k)}
            style={{flex:1,padding:"8px",border:`1px solid ${format===k?"#7c3aed":"var(--border-2)"}`,
              background:format===k?"var(--blue-bg)":"none",borderRadius:8,color:"var(--text)",
              cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>
            {l}
          </button>
        ))}
      </div>
      <div style={{background:"var(--bg)",borderRadius:8,padding:"10px 12px",marginBottom:16,fontSize:11,color:"var(--text-3)",lineHeight:1.7}}>
        <div style={{marginBottom:4,fontWeight:600,color:"var(--text-2)"}}>{t("Conținut export:")}</div>
        <div>• {t("Plan tratament activ — medicament, administrare, indicație")}</div>
        <div>• {t("Jurnal — medicamentele administrate")}</div>
        <div>• {t("Simptome și notițe")}</div>
        <div>• {t("Valori medicale (tensiune, glicemie, greutate) dacă există")}</div>
      </div>
      <button className="bp" style={{width:"100%",fontSize:14,padding:"11px"}}
        onClick={format==="pdf"?exportPDF:exportExcel}>
        {format==="pdf"?"📄 "+t("Generează PDF"):"📊 "+t("Descarcă Excel")}
      </button>
    </div>
  );
}

// Raport medical unificat — tratament + analize + evenimente + valori, pe o perioadă aleasă.
// Util de dus la un consult: un singur PDF cu tot ce s-a întâmplat în intervalul respectiv.
export function ExportRaportComplet({meds, medLogs, symptoms, patient, metrics, events, analysisDocs, onClose}) {
  const todayStr = new Date().toISOString().slice(0,10);
  const threeMonthsAgo = (()=>{ const d=new Date(); d.setMonth(d.getMonth()-3); return d.toISOString().slice(0,10); })();
  const [from,setFrom] = useState(threeMonthsAgo);
  const [to,setTo] = useState(todayStr);

  // Descriere compactă a administrării (variantă locală — helperul complet e în alt export)
  function medDesc(med){
    if(med.scheduleType==="ondemand") return t("La nevoie");
    const sched=med.schedule||[];
    const times=sched.map(s=>`${s.time||""}${s.dose?` ×${s.dose}`:""}`.trim()).filter(Boolean);
    if(med.scheduleType==="interval_hours") return `${t("o dată la")} ${med.intervalHours||8}h`;
    if(med.scheduleType==="cycle") return `${t("ciclu")} ${med.cyclicOn||21}/${med.cyclicOff||7} ${t("zile")}${times.length?` · ${times.join(", ")}`:""}`;
    if(med.scheduleType==="specific_days"){
      const days=(med.weekDays||[]).map(d=>t(["Lun","Mar","Mie","Joi","Vin","Sâm","Dum"][d])).join(", ");
      return `${days}${times.length?` · ${times.join(", ")}`:""}`;
    }
    return times.length?times.join(", "):t("zilnic");
  }

  function exportPDF() {
    if(from>to){ fmAlert(t("Data de început e după data de sfârșit.")); return; }
    const inRange = (iso)=> iso && iso>=from && iso<=to;
    const MON=["Ian","Feb","Mar","Apr","Mai","Iun","Iul","Aug","Sep","Oct","Nov","Dec"].map(m=>t(m));
    const fmtChartDate = iso => { const[y,m,d]=iso.split("-"); return `${+d}${MON[+m-1]}'${y.slice(2)}`; };

    let html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t("Raport")}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:22px;color:#1f2937;font-size:12px}
        h1{font-size:19px;margin:0 0 2px}
        h2{font-size:14px;margin:22px 0 6px;border-bottom:2px solid #1e293b;padding-bottom:3px;color:#1e293b}
        .sub{color:#6b7280;font-size:11px;margin-bottom:2px}
        table{width:100%;border-collapse:collapse;margin-bottom:8px}
        th{background:#1e293b;color:#fff;padding:5px 7px;text-align:left;font-size:10px}
        td{border:1px solid #e2e8f0;padding:4px 7px;font-size:10px;vertical-align:top}
        .muted{color:#9ca3af}
        .hi{color:#c02020;font-weight:600}
        .lo{color:#b07800;font-weight:600}
        .ok{color:#0a7a4a}
        .empty{color:#9ca3af;font-style:italic;font-size:11px;margin-bottom:8px}
        @media print{body{margin:8px}}
      </style></head><body>`;

    html+=`<h1>${t("Raport")}</h1>`;
    if(patient?.name) html+=`<div class="sub">${t("Pacient:")} <strong>${escapeHtml(patient.name)}</strong>${patient.dob?` · ${t("Data nașterii")}: ${escapeHtml(patient.dob)}`:""}${patient.bloodType?` · ${t("Grupă:")} ${escapeHtml(patient.bloodType)}`:""}</div>`;
    if(patient?.allergies) html+=`<div class="sub">Alergii: ${escapeHtml(patient.allergies)}</div>`;
    // Diagnostice/afecțiuni cunoscute
    const conds = (patient?.conditions||[]).filter(c=>c&&c.text);
    if(conds.length) html+=`<div class="sub">Diagnostice: ${conds.map(c=>escapeHtml(c.text)+(c.date?` (${escapeHtml(fmtD(c.date))})`:"")).join(", ")}</div>`;
    html+=`<div class="sub">${t("Perioada:")} <strong>${fmtD(from)} – ${fmtD(to)}</strong> · ${t("Generat:")} ${new Date().toLocaleDateString("ro-RO")}</div>`;

    // ── Tratament activ ──
    html+=`<h2>${t("Plan de tratament activ")}</h2>`;
    const activeMeds = meds.filter(m=>m.active);
    if(activeMeds.length){
      html+=`<table><thead><tr><th>${t("Medicament")}</th><th>${t("Administrare")}</th><th>${t("Indicație")}</th></tr></thead><tbody>`;
      activeMeds.forEach(med=>{
        html+=`<tr><td><strong>${escapeHtml(med.name)}</strong></td><td>${escapeHtml(medDesc(med))}</td><td class="muted">${escapeHtml(med.indication)||"—"}</td></tr>`;
      });
      html+=`</tbody></table>`;
    } else html+=`<div class="empty">${t("Niciun tratament activ.")}</div>`;

    // ── Aderență la tratament în perioadă ──
    const takenByMed = {};
    Object.entries(medLogs||{}).forEach(([dk,log])=>{
      if(!inRange(dk) || !log) return;
      Object.entries(log).forEach(([medId,doses])=>{
        (doses||[]).forEach(d=>{ if(d && d.status==="done"){ takenByMed[medId]=(takenByMed[medId]||0)+1; } });
      });
    });
    const adherenceRows = meds.filter(m=>takenByMed[m.id]).map(m=>`<tr><td>${escapeHtml(m.name)}</td><td>${takenByMed[m.id]} doze confirmate</td></tr>`).join("");
    if(adherenceRows){
      html+=`<h2>${t("Doze administrate în perioadă")}</h2><table><thead><tr><th>${t("Medicament")}</th><th>${t("Confirmate")}</th></tr></thead><tbody>${adherenceRows}</tbody></table>`;
    }

    // ── Analize în perioadă — matrice: analiză pe rând, dată pe coloană (ca în Evoluție) ──
    const labDocs = (analysisDocs||[]).filter(ad=>inRange(ad.date));
    const byAnalyte = {};
    const datesWithValues = new Set();
    labDocs.forEach(ad=>{ (ad.values||[]).forEach(v=>{
      if(!v.name) return;
      datesWithValues.add(ad.date);
      if(!byAnalyte[v.name]) byAnalyte[v.name] = {unit:v.unit||"", ref:v.referenceRange||"", byDate:{}};
      byAnalyte[v.name].byDate[ad.date] = {value:v.value, status:v.status};
      if(v.unit && !byAnalyte[v.name].unit) byAnalyte[v.name].unit = v.unit;
      if(v.referenceRange && !byAnalyte[v.name].ref) byAnalyte[v.name].ref = v.referenceRange;
    });});
    // Doar datele care au efectiv valori (fără coloană goală, ex. un document fără analize extrase)
    const labDates = [...datesWithValues].sort((a,b)=>b.localeCompare(a));
    const labNames = Object.keys(byAnalyte).sort((a,b)=>a.localeCompare(b,"ro"));
    html+=`<h2>${t("Analize de laborator")}</h2>`;
    if(labNames.length){
      const head = `<tr><th style="text-align:left">${t("Analiză")}</th><th>${t("U.M.")}</th><th>${t("Interval ref.")}</th>${labDates.map(d=>`<th>${fmtD(d)}</th>`).join("")}</tr>`;
      const body = labNames.map(name=>{
        const a = byAnalyte[name];
        const cells = labDates.map(d=>{
          const e = a.byDate[d];
          if(!e) return `<td class="muted">—</td>`;
          const cls = e.status==="high"?"hi":e.status==="low"?"lo":e.status==="normal"?"ok":"";
          return `<td class="${cls}">${escapeHtml(e.value)}</td>`;
        }).join("");
        return `<tr><td style="text-align:left;font-weight:600">${escapeHtml(name)}</td><td class="muted">${escapeHtml(a.unit)||"—"}</td><td class="muted">${escapeHtml(a.ref)||"—"}</td>${cells}</tr>`;
      }).join("");
      html+=`<table><thead>${head}</thead><tbody>${body}</tbody></table>`;
    } else html+=`<div class="empty">${t("Nicio analiză în perioada selectată.")}</div>`;

    // ── Evenimente medicale (programări/consultații) ──
    const evList = Object.entries(events||{}).filter(([k])=>inRange(k))
      .flatMap(([k,evs])=>(evs||[]).map(e=>({...e,date:k})))
      .sort((a,b)=>b.date.localeCompare(a.date));
    html+=`<h2>${t("Consultații și programări")}</h2>`;
    if(evList.length){
      html+=`<table><thead><tr><th>${t("Data")}</th><th>${t("Specialitate/Titlu")}</th><th>${t("Medic / Unitate")}</th><th>${t("Observații")}</th></tr></thead><tbody>`;
      evList.forEach(e=>{
        const title = e.isNote ? t("Notiță") : (e.spec||e.title||"—");
        const drUnit = [e.dr,e.hospital].filter(Boolean).map(escapeHtml).join(" · ")||"—";
        html+=`<tr><td style="white-space:nowrap">${fmtD(e.date)}${e.time?` ${escapeHtml(e.time)}`:""}</td><td>${escapeHtml(title)}</td><td>${drUnit}</td><td>${escapeHtml(e.visitNote)||"—"}</td></tr>`;
      });
      html+=`</tbody></table>`;
    } else html+=`<div class="empty">${t("Nicio consultație sau programare în perioada selectată.")}</div>`;

    // ── Simptome ──
    const sxList = (symptoms||[]).filter(s=>inRange(s.date)).sort((a,b)=>b.date.localeCompare(a.date));
    if(sxList.length){
      html+=`<h2>${t("Simptome raportate")}</h2><table><thead><tr><th>${t("Data")}</th><th>${t("Simptome")}</th></tr></thead><tbody>`;
      sxList.forEach(s=>{ const items=(s.items||[]).join(", "); if(items) html+=`<tr><td style="white-space:nowrap">${fmtD(s.date)}</td><td>${escapeHtml(items)}</td></tr>`; });
      html+=`</tbody></table>`;
    }

    // ── Valori măsurate (tensiune/glicemie/greutate) — grafice SVG ──
    const allMetrics=(metrics||[]).filter(m=>inRange(m.date));
    const tensAll=allMetrics.filter(m=>m.ts_sys!=null||m.ts_dia!=null).sort((a,b)=>a.date.localeCompare(b.date));
    const glicAll=allMetrics.filter(m=>m.glicemie!=null).sort((a,b)=>a.date.localeCompare(b.date));
    const greutAll=allMetrics.filter(m=>m.greutate!=null).sort((a,b)=>a.date.localeCompare(b.date));
    function makeSVGChart(title,data,series){
      if(!data||data.length===0) return "";
      const W=520,H=130,PL=42,PR=12,PT=20,PB=28,cW=W-PL-PR,cH=H-PT-PB;
      const allVals=series.flatMap(s=>data.map(p=>p[s.key]).filter(v=>v!=null));
      if(allVals.length===0) return "";
      const minV=Math.min(...allVals),maxV=Math.max(...allVals),pad=(maxV-minV)*0.1||5,lo=minV-pad,hi=maxV+pad;
      const xs=i=>PL+(data.length>1?i*cW/(data.length-1):cW/2), ys=v=>PT+cH-(v-lo)/(hi-lo)*cH;
      const hasLegend=series.length>1, svgH=hasLegend?H+22:H;
      let s=`<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${svgH}" style="display:block;margin-bottom:16px;border:1px solid #e5e7eb;background:#fff">`;
      s+=`<text x="${PL}" y="13" font-size="10" font-weight="bold" fill="#111">${title}</text>`;
      [lo,(lo+hi)/2,hi].forEach(v=>{ const y=ys(v); s+=`<line x1="${PL}" y1="${y.toFixed(1)}" x2="${W-PR}" y2="${y.toFixed(1)}" stroke="#e5e7eb" stroke-width="0.8"/><text x="${PL-4}" y="${(y+4).toFixed(1)}" font-size="8" fill="#6b7280" text-anchor="end">${Math.round(v)}</text>`; });
      const step=Math.max(1,Math.floor(data.length/8));
      data.forEach((p,i)=>{ if(i%step===0||i===data.length-1) s+=`<text x="${xs(i).toFixed(1)}" y="${(H-4).toFixed(1)}" font-size="8" fill="#6b7280" text-anchor="middle">${fmtChartDate(p.date)}</text>`; });
      series.forEach((sr,si)=>{
        const pts=data.map((p,i)=>p[sr.key]!=null?`${xs(i).toFixed(1)},${ys(p[sr.key]).toFixed(1)}`:null).filter(Boolean);
        if(pts.length>1) s+=`<polyline points="${pts.join(" ")}" fill="none" stroke="${sr.color}" stroke-width="2" stroke-linejoin="round"/>`;
        data.forEach((p,i)=>{ if(p[sr.key]!=null) s+=`<circle cx="${xs(i).toFixed(1)}" cy="${ys(p[sr.key]).toFixed(1)}" r="3" fill="${sr.color}"/>`; });
        if(hasLegend){ const lx=PL+si*160; s+=`<line x1="${lx}" y1="${H+12}" x2="${lx+12}" y2="${H+12}" stroke="${sr.color}" stroke-width="2"/><text x="${lx+16}" y="${H+16}" font-size="9" fill="#374151">${sr.label}</text>`; }
      });
      return s+`</svg>`;
    }
    const tensChart=makeSVGChart(t("Tensiune arterială"),tensAll,[{key:"ts_sys",color:"#3b82f6",label:t("Sistolică")},{key:"ts_dia",color:"#f97316",label:t("Diastolică")}]);
    const glicChart=makeSVGChart(t("Glicemie"),glicAll,[{key:"glicemie",color:"#f59e0b",label:t("Glicemie")}]);
    const greutChart=makeSVGChart(t("Greutate"),greutAll,[{key:"greutate",color:"#10b981",label:t("Greutate")}]);
    if(tensChart||glicChart||greutChart) html+=`<h2>${t("Valori măsurate")}</h2>`+tensChart+glicChart+greutChart;

    html+=`<div style="margin-top:22px;font-size:9px;color:#9ca3af">${t("Raport generat automat din aplicația Fișă Medicală. Nu înlocuiește un document medical oficial.")}</div>`;
    html+=`</body></html>`;
    const win=window.open("","_blank");
    if(!win){ fmAlert(t("Permite ferestrele pop-up pentru a genera raportul.")); return; }
    win.document.write(html);
    win.document.close();
    win.print();
    onClose();
  }

  return (
    <div>
      <div style={{background:"var(--bg)",borderRadius:8,padding:"10px 12px",marginBottom:16,fontSize:11,color:"var(--text-3)",lineHeight:1.7}}>
        <div style={{marginBottom:4,fontWeight:600,color:"var(--text-2)"}}>{t("Un singur PDF, util de dus la consult, cu tot ce s-a întâmplat în perioada aleasă:")}</div>
        <div>• {t("Planul de tratament activ + dozele administrate")}</div>
        <div>• {t("Analizele de laborator (cu interval de referință)")}</div>
        <div>• {t("Consultațiile și programările")}</div>
        <div>• {t("Simptomele raportate")}</div>
        <div>• {t("Graficele de tensiune, glicemie, greutate")}</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
        <div>
          <div className="lbl">{t("De la")}</div>
          <DateField value={from} onChange={setFrom}/>
        </div>
        <div>
          <div className="lbl">{t("Până la")}</div>
          <DateField value={to} onChange={setTo}/>
        </div>
      </div>
      <button className="bp" style={{width:"100%",fontSize:14,padding:"11px"}} onClick={exportPDF}>📄 {t("Generează raportul")}</button>
    </div>
  );
}

